import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import {IconoirCustomMessageAdd, IconoirEditPencil} from '@ey-xd/motif-icon';
import queryString from 'query-string';

import OutputText from '@ey/canvascoreservices/OutputText';

import {COMMENT} from './utils/constants';
import Utility from '../../../util/utilityfunctions';
import {maxLengthInputs} from '../../../util/uiconstants';
import {currentResource, labels} from '../../../util/utils';

import Modal from '../../CoreWrappers/Modal';
import Button from '../../CoreWrappers/Button';
import TextArea from '../../CoreWrappers/TextArea';
import useForm from '../../CoreWrappers/Form/useForm';

import LabelAndDate from '../LabelAndDate';

import ErrorSummary from '../ErrorSummary/ErrorSummary';

import {setIsModalOpen} from '../../../actions/AccessGroups/SetIsModalOpen';
import {updateGroupComponentAccount} from '../../../actions/Reconciliations';

import StyledCommentModal from './StyledCommentModal';

const CommentModal = ({rationale, rationaleModifyDate, rationaleModifyBy, isEdit, onSaveComment}) => {
	const [show, setShow] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const dispatch = useDispatch();
	const onHide = () => setShow(false);

	const setModalErrors = (val) => dispatch(clearErrors()).then(() => dispatch(setIsModalOpen(val)));

	const initialValues = {
		[COMMENT]: rationale ?? ''
	};

	const schema = {
		[COMMENT]: {required: true, maxLength: maxLengthInputs[COMMENT]}
	};

	const onSubmit = () => {
		if (onSaveComment) {
			setIsLoading(true);
			onSaveComment(values[COMMENT])
				.then(() => {
					setIsLoading(false);
					setShow(false);
				})
				.finally(() => setIsLoading(false));
		}
	};

	const {values, errors, handleChange, handleSubmit, handleReset, isFormValid} = useForm({
		initialValues,
		schema,
		onSubmit,
		labels: currentResource.formLabels
	});

	const renderTrigger = () => {
		const props = {
			svg: isEdit ? IconoirEditPencil : IconoirCustomMessageAdd,
			variant: 'ghost',
			title: isEdit ? labels.edit : labels.createComment,
			onClick: () => {
				setShow(true);
				setModalErrors(true);
			}
		};

		return <Button {...props} />;
	};

	const renderLastModified = () => {
		const labelAndDateProps = {
			date: rationaleModifyDate,
			label: labels.lastModified,
			author: rationaleModifyBy,
			body2: true,
			light: true
		};

		return <LabelAndDate {...labelAndDateProps} />;
	};

	const renderModal = () => {
		const renderInstructional = () => (
			<OutputText light>
				{Utility.createRichTextElement(
					isEdit ? labels.editCommentInstructionalText : labels.createCommentInstructionalText
				)}
			</OutputText>
		);

		const renderTextArea = (id) => {
			const props = {
				id,
				value: values[id],
				textAreaEditorHeaderLabel: labels.addComment,
				showHeadingLabel: true,
				maxLength: maxLengthInputs[id],
				textAreaEditorLabel: '',
				rows: 8,
				onChange: (e) => {
					handleChange(id, e.target.value ?? '');
				}
			};

			if (errors[id]) props.textAreaError = props.textAreaErrorMessage = errors[id]?.message;

			return <TextArea {...props} />;
		};

		const props = {
			show,
			modalsize: 'small',
			title: isEdit ? labels.editComment : labels.commentTitle,
			labels: {
				modalConfirmBtnLabel: labels.save,
				modalCancelBtnLabel: labels.cancelLabel,
				modalCloseBtnTitletip: labels.closeLabel
			},
			onClose: () => {
				handleReset();
				onHide();
				setModalErrors(false);
			},
			onOkClick: handleSubmit,
			isOkDisabled: isLoading,
			isCancelDisabled: isLoading,
			closeButtonProps: {
				disabled: isLoading
			},
			isLoading
		};

		return (
			show && (
				<Modal {...props}>
					<ErrorSummary />
					<StyledCommentModal>
						{renderInstructional()}
						{renderTextArea(COMMENT)}
						{isEdit && renderLastModified()}
					</StyledCommentModal>
				</Modal>
			)
		);
	};

	return (
		<>
			{renderTrigger()}
			{renderModal()}
		</>
	);
};

CommentModal.propTypes = {};

export default CommentModal;
